// ============================================================
// YOM Connect — the install handshake marketplace apps use instead of
// pasted keys (docs/integrations/connect.md, INTEGRATIONS-ROADMAP.md §4.5).
// The single point of change for: which platforms may connect, where
// each may send the merchant back (return-URL allowlist), what an
// install is granted, and the PKCE / code formats. Used by the API
// (routes/v2/yom-connect.ts) and the dashboard's /app/connect page.
// ============================================================
import { appPath } from '../constants/dashboard-paths.js';
import { timingSafeEqual } from '../analytics/crypto.js';
export const CONNECT_PLATFORM_IDS = [
    'shopify',
    'wix',
    'webflow',
    'bigcommerce',
    'squarespace',
    'woocommerce',
    'wordpress',
    'stripe',
];
export const CONNECT_PLATFORMS = {
    shopify: { id: 'shopify', name: 'Shopify', client: 'confidential', store: 'host' },
    wix: { id: 'wix', name: 'Wix', client: 'confidential', store: 'id' },
    webflow: { id: 'webflow', name: 'Webflow', client: 'confidential', store: 'id' },
    bigcommerce: { id: 'bigcommerce', name: 'BigCommerce', client: 'confidential', store: 'id' },
    squarespace: { id: 'squarespace', name: 'Squarespace', client: 'confidential', store: 'id' },
    woocommerce: { id: 'woocommerce', name: 'WooCommerce', client: 'public', store: 'host' },
    // The same plugin (yom-analytics) on a WordPress site without WooCommerce.
    wordpress: { id: 'wordpress', name: 'WordPress', client: 'public', store: 'host' },
    // Stripe: `store` is the hub's opaque endpoint id for the merchant's webhook (no Stripe OAuth).
    stripe: { id: 'stripe', name: 'Stripe', client: 'confidential', store: 'id' },
};
export function connectPlatform(id) {
    return typeof id === 'string' && Object.hasOwn(CONNECT_PLATFORMS, id)
        ? CONNECT_PLATFORMS[id]
        : null;
}
/** The integration hub (decision D6): every hosted platform returns to its own prefix there. */
export const CONNECT_HUB_ORIGIN = 'https://connect.yourownmetrics.aapastech.com';
/**
 * Default return-URL allowlist: URL prefixes with `{platform}` / `{store}` placeholders.
 * `{store}` is only substituted for host-type stores (the merchant's own site, WooCommerce).
 * A deployment overrides any platform (or `*`, every platform) with the CONNECT_RETURN_ALLOWLIST
 * var, e.g. staging's hub host or the local e2e receiver. Config, never per-customer code.
 */
export const DEFAULT_CONNECT_RETURN_ALLOWLIST = {
    '*': [`${CONNECT_HUB_ORIGIN}/{platform}/`],
    woocommerce: ['https://{store}/wp-admin/'],
    wordpress: ['https://{store}/wp-admin/'],
};
/** Parse the CONNECT_RETURN_ALLOWLIST var (JSON) over the defaults; bad JSON keeps the defaults. */
export function connectAllowlist(raw) {
    if (!raw) {
        return DEFAULT_CONNECT_RETURN_ALLOWLIST;
    }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('not an object');
        }
        const out = { ...DEFAULT_CONNECT_RETURN_ALLOWLIST };
        for (const [k, v] of Object.entries(parsed)) {
            if (Array.isArray(v) && v.every(s => typeof s === 'string')) {
                out[k] = v;
            }
        }
        return out;
    }
    catch {
        console.warn('[connect] CONNECT_RETURN_ALLOWLIST is not valid JSON; using defaults');
        return DEFAULT_CONNECT_RETURN_ALLOWLIST;
    }
}
/** Store ids: platform ids / GIDs / host names. Lower-cased hosts for host-type platforms. */
const STORE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;
const STORE_HOST_RE = /^(?=.{1,64}$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;
/** Normalised store id, or null when it is not one this platform can have. */
export function normalizeConnectStore(platform, store) {
    if (typeof store !== 'string') {
        return null;
    }
    if (platform.store === 'host') {
        const host = store.trim().toLowerCase();
        return STORE_HOST_RE.test(host) ? host : null;
    }
    const id = store.trim();
    return STORE_ID_RE.test(id) ? id : null;
}
/**
 * Is `returnUrl` allowed for this platform (and store)? Same scheme, host and port as an
 * allowlisted prefix, path under it, no credentials and no fragment. `state` and other query
 * parameters are the integration's own and are allowed.
 */
export function connectReturnAllowed(allowlist, platform, store, returnUrl) {
    if (typeof returnUrl !== 'string' || returnUrl.length > 1024) {
        return false;
    }
    let url;
    try {
        url = new URL(returnUrl);
    }
    catch {
        return false;
    }
    if (url.username || url.password || url.hash || !['https:', 'http:'].includes(url.protocol)) {
        return false;
    }
    const patterns = allowlist[platform.id] ?? allowlist['*'] ?? [];
    return patterns.some(pattern => {
        if (pattern.includes('{store}') && platform.store !== 'host') {
            return false;
        }
        let allowed;
        try {
            allowed = new URL(pattern.replaceAll('{platform}', platform.id).replaceAll('{store}', store));
        }
        catch {
            return false;
        }
        return (url.protocol === allowed.protocol &&
            url.host === allowed.host &&
            url.pathname.startsWith(allowed.pathname));
    });
}
/** What an install gets — the consent screen lists exactly these (one source of truth). */
export const CONNECT_KEY_GRANTS = [
    {
        type: 'pk',
        scopes: ['ingest'],
        description: 'A publishable key, to send storefront events from shoppers’ browsers',
    },
    {
        type: 'sk',
        scopes: ['server', 'read'],
        description: 'A secret key, to send orders and refunds from the store’s server and to check the connection is healthy (read reports)',
    },
];
/** Label on both keys of an install (shown in API keys; used to find them). */
export function connectKeyLabel(platform, store) {
    return `integration:${platform}:${store}`.slice(0, 100);
}
/** One-time code lifetime (seconds): the hub exchanges it immediately. */
export const CONNECT_CODE_TTL_SECONDS = 60;
export const CONNECT_CODE_PREFIX = 'yom_cc_';
/** Install ids: `ins_<32 hex>`. */
export const CONNECT_INSTALL_ID_PREFIX = 'ins';
export const CONNECT_INSTALL_ID_RE = /^ins_[0-9a-f]{32}$/;
/** PKCE (RFC 7636): verifier is 43-128 chars of [A-Za-z0-9-._~]; challenge is S256 base64url. */
export const PKCE_VERIFIER_RE = /^[A-Za-z0-9\-._~]{43,128}$/;
export const PKCE_CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;
export async function pkceChallenge(verifier) {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
    let bin = '';
    for (const b of digest) {
        bin += String.fromCharCode(b);
    }
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export async function pkceVerify(verifier, challenge) {
    return (typeof verifier === 'string' &&
        PKCE_VERIFIER_RE.test(verifier) &&
        timingSafeEqual(await pkceChallenge(verifier), challenge));
}
/** Dashboard page the integration sends the merchant to. */
export const DASHBOARD_CONNECT_PATH = appPath('connect');
/** The connect URL an integration builds (also used by docs and tests). */
export function connectUrl(dashboardOrigin, p) {
    const q = new URLSearchParams({
        platform: p.platform,
        store: p.store,
        return: p.returnUrl,
        state: p.state,
        code_challenge: p.codeChallenge,
        code_challenge_method: 'S256',
    });
    return `${dashboardOrigin.replace(/\/+$/, '')}${DASHBOARD_CONNECT_PATH}?${q}`;
}
/** Where the merchant goes back to: the return URL plus `code` and the untouched `state`. */
export function connectRedirect(returnUrl, p) {
    const url = new URL(returnUrl);
    // Only this answer's parameters: never hand back a code / error / state already on the URL.
    for (const k of ['code', 'error', 'state']) {
        url.searchParams.delete(k);
    }
    if (p.code) {
        url.searchParams.set('code', p.code);
    }
    if (p.error) {
        url.searchParams.set('error', p.error);
    }
    url.searchParams.set('state', p.state);
    return url.toString();
}
/** `state` is opaque to YOM; bounded so it fits a URL. */
export const CONNECT_STATE_RE = /^[\x21-\x7e]{8,512}$/;
//# sourceMappingURL=index.js.map